import { Accordion } from '@delmonicos/shared-design-system-react/src/components/accordion/accordion';
import {
  Divider,
  DividerOrientation,
} from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import { Spinner } from '@delmonicos/shared-design-system-react/src/components/spinner/spinner';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { OcppLogMessageFormatted } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/logger/ocpp-logs-messsages.interface';
import { useVirtualizer } from '@tanstack/react-virtual';
import { DateArg } from 'date-fns';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { LogItemTitle } from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-list/log-item-title/log-item-title.component';
import LogItem from '@app/feature/charge-point/charge-point-details/tabs/maintenance-tab/log-card/log-list/log-item/log-item.component';

import * as css from './log-list.module.scss';

interface LogListComponentProps {
  logs: OcppLogMessageFormatted[];
  isFetching: boolean;
  isLoading: boolean;
  nextOffset?: DateArg<Date> & {};
  onSeeMore: () => void;
}

export const LogListComponent = ({
  logs,
  isFetching,
  isLoading,
  nextOffset,
  onSeeMore,
}: LogListComponentProps): React.JSX.Element => {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 45,
  });

  const items = virtualizer.getVirtualItems();

  return (
    <Stack gap={GapEnum.L} flex={1} direction="column" alignItems="center">
      <div ref={parentRef} className={css.parentContainer}>
        <div
          className={css.scrollContainer}
          style={{
            height: virtualizer.getTotalSize(),
          }}>
          <div
            className={css.itemsContainer}
            style={{
              transform: `translateY(${items[0]?.start ?? 0}px)`,
            }}>
            {items.map((virtualRow) => {
              const log = logs.at(virtualRow.index);

              if (!log) {
                return null;
              }

              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}>
                  <Accordion title={<LogItemTitle log={log} />} isOpen={false}>
                    <LogItem log={log} />
                  </Accordion>
                  {virtualRow.index < logs.length - 1 && (
                    <div className={css.divider}>
                      <Divider orientation={DividerOrientation.Vertical} />
                    </div>
                  )}
                </div>
              );
            })}
            {(isFetching || isLoading) && <Spinner className={css.spinner} />}
            {!isFetching && !!nextOffset && (
              <Link onClick={onSeeMore} className={css.seeMore}>
                {t('$.charge-point-details.maintenance.see-more')}
                <Icon name="chevron-down" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </Stack>
  );
};
